import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  SwapHoriz as SwapHorizIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Inventory as InventoryIcon,
  Visibility,
  Edit,
  Close,
  Person,
  Save,
  Add,
  CompareArrows,
  LocalShipping,
} from '@mui/icons-material';

const ExchangeReplacement = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    exchangeType: '',
    searchTerm: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editFormData, setEditFormData] = useState({
    exchangeStatus: '',
    exchangeType: '',
    newProduct: '',
    exchangeReason: '',
    pickupDate: '',
    deliveryDate: '',
    notes: '',
  });

  const sampleData = [
    {
      sno: 1,
      exchangeId: 'EXC001',
      orderId: 'ORD001',
      exchangeDate: '2025-01-01',
      customerName: 'Amit Gupta',
      customerPhone: '+91 98765 43210',
      originalProduct: 'Samsung Galaxy S24 (128GB)',
      newProduct: 'Samsung Galaxy S24 (256GB)',
      quantity: 1,
      priceDifference: 10000,
      exchangeStatus: 'Completed',
      exchangeType: 'Upgrade',
      exchangeReason: 'Storage Upgrade',
      pickupDate: '2025-01-02',
      deliveryDate: '2025-01-04',
      notes: 'Customer requested higher storage',
    },
    {
      sno: 2,
      exchangeId: 'EXC002',
      orderId: 'ORD002',
      exchangeDate: '2025-01-02',
      customerName: 'Priya Sharma',
      customerPhone: '+91 98765 43211',
      originalProduct: 'iPhone 15 Pro (Blue)',
      newProduct: 'iPhone 15 Pro (Black)',
      quantity: 1,
      priceDifference: 0,
      exchangeStatus: 'In Progress',
      exchangeType: 'Color Change',
      exchangeReason: 'Color Preference',
      pickupDate: '2025-01-03',
      deliveryDate: '',
      notes: 'Customer changed color preference',
    },
    {
      sno: 3,
      exchangeId: 'EXC003',
      orderId: 'ORD003',
      exchangeDate: '2025-01-03',
      customerName: 'Rajesh Kumar',
      customerPhone: '+91 98765 43212',
      originalProduct: 'OnePlus 12 (8GB)',
      newProduct: 'OnePlus 12 (12GB)',
      quantity: 2,
      priceDifference: 8000,
      exchangeStatus: 'Pending',
      exchangeType: 'Specification Change',
      exchangeReason: 'RAM Upgrade',
      pickupDate: '',
      deliveryDate: '',
      notes: 'Customer wants higher RAM variant',
    },
    {
      sno: 4,
      exchangeId: 'EXC004',
      orderId: 'ORD004',
      exchangeDate: '2025-01-04',
      customerName: 'Sneha Patel',
      customerPhone: '+91 98765 43213',
      originalProduct: 'Google Pixel 8',
      newProduct: 'Google Pixel 8 Pro',
      quantity: 1,
      priceDifference: 15000,
      exchangeStatus: 'Approved',
      exchangeType: 'Model Upgrade',
      exchangeReason: 'Feature Requirements',
      pickupDate: '2025-01-05',
      deliveryDate: '2025-01-07',
      notes: 'Customer needs Pro features',
    },
    {
      sno: 5,
      exchangeId: 'EXC005',
      orderId: 'ORD005',
      exchangeDate: '2025-01-05',
      customerName: 'Vikram Singh',
      customerPhone: '+91 98765 43214',
      originalProduct: 'Xiaomi 14',
      newProduct: 'Xiaomi 14 Ultra',
      quantity: 1,
      priceDifference: 20000,
      exchangeStatus: 'Rejected',
      exchangeType: 'Model Upgrade',
      exchangeReason: 'Budget Constraints',
      pickupDate: '',
      deliveryDate: '',
      notes: 'Customer cannot afford price difference',
    },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    // Here you would typically make an API call to update the exchange
    console.log('Saving exchange:', selectedExchange?.exchangeId, editFormData);
    
    // For demo purposes, we'll just close the dialog
    // In a real app, you'd update the exchange in your state/API
    setEditDialogOpen(false);
    setSelectedExchange(null);
    setEditFormData({
      exchangeStatus: '',
      exchangeType: '',
      newProduct: '',
      exchangeReason: '',
      pickupDate: '',
      deliveryDate: '',
      notes: '',
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  // Search handler - applies all filters
  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    console.log('Search triggered with filters:', {
      searchTerm: searchTerm,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      status: filters.status,
      exchangeType: filters.exchangeType
    }); // Debug log
    // Simulate API call delay
    setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm: searchTerm }));
      setSearchLoading(false);
    }, 500);
  };

  // View and Edit action handlers
  const handleView = (exchange) => {
    setSelectedExchange(exchange);
    setViewDialogOpen(true);
  };

  const handleEdit = (exchange) => {
    setSelectedExchange(exchange);
    setEditFormData({
      exchangeStatus: exchange.exchangeStatus,
      exchangeType: exchange.exchangeType,
      newProduct: exchange.newProduct,
      exchangeReason: exchange.exchangeReason,
      pickupDate: exchange.pickupDate,
      deliveryDate: exchange.deliveryDate,
      notes: exchange.notes,
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedExchange(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedExchange(null);
    setEditFormData({
      exchangeStatus: '',
      exchangeType: '',
      newProduct: '',
      exchangeReason: '',
      pickupDate: '',
      deliveryDate: '',
      notes: '',
    });
  };

  // Optimized filter function with memoization - only works when search is performed
  const getFilteredData = useCallback(() => {
    // If no search has been performed, return all data
    if (!hasSearched) {
      return sampleData;
    }

    let filteredData = [...sampleData];

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.exchangeId.toLowerCase().includes(searchLower) ||
        item.orderId.toLowerCase().includes(searchLower) ||
        item.customerName.toLowerCase().includes(searchLower) ||
        item.originalProduct.toLowerCase().includes(searchLower) ||
        item.newProduct.toLowerCase().includes(searchLower) ||
        item.exchangeReason.toLowerCase().includes(searchLower)
      );
    }

    // Apply date filters
    if (filters.fromDate) {
      filteredData = filteredData.filter(item => new Date(item.exchangeDate) >= new Date(filters.fromDate));
    }
    if (filters.toDate) {
      filteredData = filteredData.filter(item => new Date(item.exchangeDate) <= new Date(filters.toDate));
    }

    // Apply status filter
    if (filters.status) {
      filteredData = filteredData.filter(item => item.exchangeStatus === filters.status);
    }

    // Apply exchange type filter
    if (filters.exchangeType) {
      filteredData = filteredData.filter(item => item.exchangeType === filters.exchangeType);
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.fromDate, filters.toDate, filters.status, filters.exchangeType]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalExchanges = filteredData.length;
  const completedExchanges = filteredData.filter(item => item.exchangeStatus === 'Completed').length;
  const inProgressExchanges = filteredData.filter(item => item.exchangeStatus === 'In Progress' || item.exchangeStatus === 'Approved').length;
  const pendingExchanges = filteredData.filter(item => item.exchangeStatus === 'Pending').length;
  const totalPriceDifference = filteredData.reduce((sum, item) => sum + item.priceDifference, 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'info';
      case 'Approved': return 'primary';
      case 'Pending': return 'warning';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const getExchangeTypeColor = (type) => {
    switch (type) {
      case 'Upgrade': return 'success';
      case 'Color Change': return 'info';
      case 'Specification Change': return 'warning';
      case 'Model Upgrade': return 'primary';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Exchange & Replacement Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Process and manage product exchanges and replacements
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            New Exchange Request
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <SwapHorizIcon sx={{ fontSize: 40, color: '#536DFE', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {totalExchanges}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Exchanges
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {completedExchanges}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Completed
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <InventoryIcon sx={{ fontSize: 40, color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                ₹{(totalPriceDifference / 100000).toFixed(2)}L
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Price Difference
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <ScheduleIcon sx={{ fontSize: 40, color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {inProgressExchanges}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              In Progress
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 3 }}>
            Exchange & Replacement Filters
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Exchange Status</InputLabel>
                <Select 
                  label="Exchange Status" 
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Exchange Type</InputLabel>
                <Select 
                  label="Exchange Type" 
                  value={filters.exchangeType}
                  onChange={(e) => {
                    setFilters({ ...filters, exchangeType: e.target.value });
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Upgrade">Upgrade</MenuItem>
                  <MenuItem value="Color Change">Color Change</MenuItem>
                  <MenuItem value="Specification Change">Specification Change</MenuItem>
                  <MenuItem value="Model Upgrade">Model Upgrade</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search Exchanges"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by exchange ID, order ID, customer name, or product..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searchLoading ? (
                        <CircularProgress size={20} color="primary" />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Search Controls */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip title="Apply all filters and search" arrow>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#536DFE',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#3F51B5',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({
                  fromDate: '',
                  toDate: '',
                  status: '',
                  exchangeType: '',
                  searchTerm: '',
                });
                setSearchTerm('');
                setHasSearched(false);
                setSearchLoading(false);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#536DFE',
                color: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#3F51B5',
                  backgroundColor: '#f0f7ff',
                }
              }}
            >
              Clear All
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Search Results Summary */}
      {hasSearched && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '1px solid #e3f2fd' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1,
                backgroundColor: '#e3f2fd',
                borderRadius: 2,
                minWidth: 'fit-content'
              }}>
                {searchLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SearchIcon sx={{ color: '#536DFE' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#536DFE' }}>
                  Search Results
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Searching exchange records...
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                      {filteredData.length} exchange{filteredData.length !== 1 ? 's' : ''} found
                    </Typography>
                    {filters.status && filters.status !== 'All Status' && (
                      <Chip
                        label={`Status: ${filters.status}`} 
                        size="small"
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.exchangeType && filters.exchangeType !== 'All Types' && (
                      <Chip
                        label={`Type: ${filters.exchangeType}`} 
                        size="small"
                        color="secondary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.fromDate && (
                      <Chip 
                        label={`From: ${filters.fromDate}`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.toDate && (
                      <Chip 
                        label={`To: ${filters.toDate}`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Exchange & Replacement Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Exchange ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Order ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Original Product</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>New Product</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Price Diff</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Searching exchange records...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow 
                      key={row.sno} 
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                      <TableCell>{row.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.exchangeId}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.orderId}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.customerName}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.customerPhone}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.originalProduct}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.newProduct}</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        color: row.priceDifference > 0 ? '#2e7d32' : row.priceDifference < 0 ? '#d32f2f' : '#666'
                      }}>
                        {row.priceDifference > 0 ? '+' : ''}₹{row.priceDifference.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.exchangeStatus} 
                          size="small"
                          color={getStatusColor(row.exchangeStatus)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.exchangeType} 
                          size="small"
                          color={getExchangeTypeColor(row.exchangeType)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(row)}
                              sx={{ color: '#536DFE' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Exchange">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              sx={{ color: '#ff9800' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No exchange records found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon />
            <Typography variant="h6" fontWeight="bold">
              Exchange & Replacement Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedExchange && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 48, height: 48 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedExchange.exchangeId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Order: {selectedExchange.orderId} | Customer: {selectedExchange.customerName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={selectedExchange.exchangeStatus} 
                      size="small" 
                      color={getStatusColor(selectedExchange.exchangeStatus)}
                    />
                    <Chip 
                      label={selectedExchange.exchangeType} 
                      size="small" 
                      color={getExchangeTypeColor(selectedExchange.exchangeType)}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Exchange Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SwapHorizIcon color="primary" />
                        Exchange Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Exchange ID:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedExchange.exchangeId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Order ID:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedExchange.orderId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Exchange Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedExchange.exchangeDate}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedExchange.quantity}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Price Difference:</Typography>
                          <Typography variant="body2" fontWeight="600" color={selectedExchange.priceDifference > 0 ? 'success.main' : selectedExchange.priceDifference < 0 ? 'error.main' : 'text.primary'}>
                            {selectedExchange.priceDifference > 0 ? '+' : ''}₹{selectedExchange.priceDifference.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Product Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CompareArrows color="primary" />
                        Product Exchange
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Original Product:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedExchange.originalProduct}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>New Product:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedExchange.newProduct}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Exchange Type:</Typography>
                          <Chip 
                            label={selectedExchange.exchangeType} 
                            size="small" 
                            color={getExchangeTypeColor(selectedExchange.exchangeType)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Exchange Status:</Typography>
                          <Chip 
                            label={selectedExchange.exchangeStatus} 
                            size="small" 
                            color={getStatusColor(selectedExchange.exchangeStatus)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Reason:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedExchange.exchangeReason}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Logistics Details */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping color="primary" />
                    Logistics & Customer Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Customer Name:</strong> {selectedExchange.customerName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Phone:</strong> {selectedExchange.customerPhone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Pickup Date:</strong> {selectedExchange.pickupDate || 'Not scheduled'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Delivery Date:</strong> {selectedExchange.deliveryDate || 'Not scheduled'}
                      </Typography>
                    </Grid>
                    {selectedExchange.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Notes:</strong> {selectedExchange.notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Exchange Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#ff9800',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit />
            <Typography variant="h6" fontWeight="bold">
              Edit Exchange/Replacement
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEditDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedExchange && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Edit exchange/replacement details for: <strong>{selectedExchange.exchangeId}</strong>
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Exchange Status</InputLabel>
                    <Select
                      value={editFormData.exchangeStatus}
                      onChange={(e) => handleEditFormChange('exchangeStatus', e.target.value)}
                      label="Exchange Status"
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Exchange Type</InputLabel>
                    <Select
                      value={editFormData.exchangeType}
                      onChange={(e) => handleEditFormChange('exchangeType', e.target.value)}
                      label="Exchange Type"
                    >
                      <MenuItem value="Upgrade">Upgrade</MenuItem>
                      <MenuItem value="Color Change">Color Change</MenuItem>
                      <MenuItem value="Specification Change">Specification Change</MenuItem>
                      <MenuItem value="Model Upgrade">Model Upgrade</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Product"
                    value={editFormData.newProduct}
                    onChange={(e) => handleEditFormChange('newProduct', e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Exchange Reason</InputLabel>
                    <Select
                      value={editFormData.exchangeReason}
                      onChange={(e) => handleEditFormChange('exchangeReason', e.target.value)}
                      label="Exchange Reason"
                    >
                      <MenuItem value="Storage Upgrade">Storage Upgrade</MenuItem>
                      <MenuItem value="Color Preference">Color Preference</MenuItem>
                      <MenuItem value="RAM Upgrade">RAM Upgrade</MenuItem>
                      <MenuItem value="Feature Requirements">Feature Requirements</MenuItem>
                      <MenuItem value="Budget Constraints">Budget Constraints</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Pickup Date"
                    type="date"
                    value={editFormData.pickupDate}
                    onChange={(e) => handleEditFormChange('pickupDate', e.target.value)}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Delivery Date"
                    type="date"
                    value={editFormData.deliveryDate}
                    onChange={(e) => handleEditFormChange('deliveryDate', e.target.value)}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={editFormData.notes}
                    onChange={(e) => handleEditFormChange('notes', e.target.value)}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>

              {/* Read-only fields */}
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Exchange Information (Read-only)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Exchange ID:</strong> {selectedExchange.exchangeId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Order ID:</strong> {selectedExchange.orderId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Customer:</strong> {selectedExchange.customerName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Original Product:</strong> {selectedExchange.originalProduct}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<Save />}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExchangeReplacement;
